import {
  Box,
  Typography,
  Stack,
  List,
  ListItem,
  ListItemText,
  styled,
} from "@mui/material";
import { FC, useMemo } from "react";
import { PanelLayout } from "../PanelLayout";
import { TResult } from "../../hooks/useDocumentListWithData";
import { TVerificationRequest } from "../../models";
import {
  ApprovedIcon,
  CancelIcon,
  PendingIcon,
} from "../../../../components/MuiIcons";
import { mainTheme } from "../../../../theme/mainTheme";

export const identificationStatusModel = {
  valid: {
    color: mainTheme.palette.success.main,
    icon: <ApprovedIcon viewBox="0 0 20 20" />,
    label: "Successful",
  },
  invalid: {
    color: mainTheme.palette.error.main,
    icon: <CancelIcon viewBox="0 0 20 20" />,
    label: "Failed",
  },
  review_needed: {
    color: mainTheme.palette.warning.main,
    icon: <PendingIcon />,
    label: "Review needed",
  },
  pending: {
    color: mainTheme.palette.warning.main,
    icon: <PendingIcon />,
    label: "Pending",
  },
};

const LabelText = styled(Typography)`
  width: 100%;
`;

const ValueText = styled(Typography)`
  width: 100%;
  display: flex;
  align-items: center;
  font-weight: 700;
  text-transform: capitalize;
  & svg {
    vertical-align: middle;
  }
`;

export const IdentificationPanel: FC<{
  data: TVerificationRequest;
  documents: TResult;
}> = ({ data, documents }) => {
  const status = data.identificationStatus || "pending";
  const model =
    identificationStatusModel[status as keyof typeof identificationStatusModel];

  // Extract applicant data from ID/Passport documents
  const applicantData = useMemo(() => {
    if (!documents?.length) return null;

    // Find ID or Passport documents
    const identityDocuments = documents.filter(
      (doc) =>
        doc.document.documentType === "Id" ||
        doc.document.documentType === "Passport"
    );

    if (!identityDocuments.length) return null;

    // Get data from the first identity document
    const firstIdentityDoc = identityDocuments[0];
    const docData = firstIdentityDoc.data?.[0]?.data;

    if (!docData) return null;

    // Calculate age from date of birth
    const calculateAge = (dateOfBirth: string) => {
      if (!dateOfBirth) return null;
      const today = new Date();
      const birthDate = new Date(dateOfBirth);
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();

      if (
        monthDiff < 0 ||
        (monthDiff === 0 && today.getDate() < birthDate.getDate())
      ) {
        age--;
      }

      return age;
    };

    return {
      name:
        docData.first_name && docData.last_name
          ? `${docData.first_name} ${docData.last_name}`
          : null,
      age: docData.date_of_birth ? calculateAge(docData.date_of_birth) : null,
      gender: docData.gender || null,
      country: docData.country || null,
    };
  }, [documents]);

  // Create compact applicant info string
  const applicantInfo = useMemo(() => {
    if (!applicantData) return null;

    const parts = [];

    if (applicantData.name) {
      parts.push(applicantData.name);
    }

    if (applicantData.age) {
      parts.push(`Age ${applicantData.age}`);
    }

    if (applicantData.gender) {
      parts.push(applicantData.gender);
    }

    if (applicantData.country) {
      parts.push(applicantData.country);
    }

    return parts.length > 0 ? parts.join(", ") : null;
  }, [applicantData]);

  // Fetch decline reasons and warnings from documents
  const identificationDetails = useMemo(() => {
    const declineReasons: string[] = [];
    const warnings: string[] = [];

    const latestIdentityDoc = documents
      ?.filter(
        (doc) =>
          doc.document.documentType === "Id" ||
          doc.document.documentType === "Passport"
      )
      .sort((a, b) => parseInt(b.document.documentId, 10) - parseInt(a.document.documentId, 10))[0];

    if (!latestIdentityDoc) {
      declineReasons.push("No document uploaded");
    } else {
      if (latestIdentityDoc.document.declineReason?.length > 0) {
        declineReasons.push(...latestIdentityDoc.document.declineReason);
      }
      if (latestIdentityDoc.document.warnings?.length > 0) {
        warnings.push(...latestIdentityDoc.document.warnings);
      }
    }

    const latestSelfie = documents
      ?.filter((doc) => doc.document.documentType === "Selfie")
      .sort((a, b) => parseInt(b.document.documentId, 10) - parseInt(a.document.documentId, 10))[0];

    if (!latestSelfie) {
      declineReasons.push("No selfie uploaded");
      declineReasons.push("No liveness check passed");
    } else {
      if (latestSelfie.document.declineReason?.length > 0) {
        declineReasons.push(...latestSelfie.document.declineReason);
      }
      if (latestSelfie.document.warnings?.length > 0) {
        warnings.push(...latestSelfie.document.warnings);
      }
    }

    return {
      declineReasons,
      warnings,
    };
  }, [documents]);

  return (
    <PanelLayout title="Identification">
      <Stack sx={{ mt: 2 }}>
        {applicantInfo && (
          <Box>
            <Typography variant="body1" fontWeight="medium">
              {applicantInfo}
            </Typography>
          </Box>
        )}
        <Stack direction="row" spacing={2}>
          <LabelText>Status</LabelText>
          <ValueText sx={{ color: model?.color }}>
            {model?.icon} {model?.label}
          </ValueText>
        </Stack>
        {identificationDetails.declineReasons.length > 0 && (
          <Stack direction="row" spacing={2} alignItems="flex-start" mt={2}>
            <LabelText>Reasons</LabelText>
            <Box sx={{ width: "100%" }}>
              <List dense sx={{ py: 0 }}>
                {identificationDetails.declineReasons.map((reason, index) => (
                  <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                    <ListItemText
                      primary={reason}
                      primaryTypographyProps={{
                        variant: "body2",
                        color: "error.main",
                        sx: { fontSize: "0.875rem" },
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Stack>
        )}
        {identificationDetails.warnings.length > 0 && (
          <Stack direction="row" spacing={2} alignItems="flex-start" mt={2}>
            <LabelText>Warnings</LabelText>
            <Box sx={{ width: "100%" }}>
              <List dense sx={{ py: 0 }}>
                {identificationDetails.warnings.map((warning, index) => (
                  <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                    <ListItemText
                      primary={warning}
                      primaryTypographyProps={{
                        variant: "body2",
                        color: "warning.main",
                        sx: { fontSize: "0.875rem" },
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Stack>
        )}
      </Stack>
    </PanelLayout>
  );
};
